import { request } from './client';
import type { AccountingSummary, UserAccountingDetail, PlayerAccountingSummary, PlayerManagerDetail } from '../types';

export const accountingApi = {
  // =====================
  // Manager View (for board managers)
  // =====================

  /**
   * Get aggregated accounting summary across all completed boards the user manages
   */
  async getSummary(): Promise<AccountingSummary> {
    return request<AccountingSummary>('/api/squareup/accounting');
  },

  /**
   * Get detailed per-board breakdown for a specific user
   */
  async getUserDetail(userId: string): Promise<UserAccountingDetail> {
    return request<UserAccountingDetail>(`/api/squareup/accounting/user/${userId}`);
  },

  /**
   * Get detailed per-board breakdown for a specific offline player
   */
  async getOfflinePlayerDetail(offlinePlayerId: string): Promise<UserAccountingDetail> {
    return request<UserAccountingDetail>(`/api/squareup/accounting/offline/${offlinePlayerId}`);
  },

  // =====================
  // Player View (self-view)
  // =====================

  /**
   * Get the current user's balances across all managers they've played with
   */
  async getMyBalances(): Promise<PlayerAccountingSummary> {
    return request<PlayerAccountingSummary>('/api/squareup/accounting/my-balances');
  },

  /**
   * Get detailed per-board breakdown for the current user with a specific manager
   */
  async getMyManagerDetail(managerId: string): Promise<PlayerManagerDetail> {
    return request<PlayerManagerDetail>(`/api/squareup/accounting/my-balances/manager/${managerId}`);
  },

  // =====================
  // Recalculate Feature
  // =====================

  /**
   * Check if the recalculate feature is enabled
   */
  async getRecalculateStatus(): Promise<{ enabled: boolean }> {
    return request<{ enabled: boolean }>('/api/squareup/accounting/recalculate/status');
  },

  /**
   * Trigger a recalculation of accounting for a specific user
   */
  async recalculateUserAccounting(userId: string, isOfflinePlayer: boolean): Promise<{ message: string; status: string }> {
    const path = isOfflinePlayer
      ? `/api/squareup/accounting/offline/${userId}/recalculate`
      : `/api/squareup/accounting/user/${userId}/recalculate`;
    return request<{ message: string; status: string }>(path, { method: 'POST' });
  },
};
